/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import keys from 'lodash/keys';
import forEach from 'lodash/forEach';
import size from 'lodash/size';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import lodashMin from 'lodash/min';
import lodashMax from 'lodash/max';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import subtract from 'lodash/subtract';
import includes from 'lodash/includes';
import isNaN from 'lodash/isNaN';
import round from 'lodash/round';

import { calendarDateToUtcString, formatMoney } from 'sarsaparilla';
import {
    isDayUseWeekendDay,
    isWeekendDay,
    isDateAfter,
    isDateBefore,
    isDateSameOrBefore,
    isDateValid,
    UTCStringToCalendarDate,
    formatUTCDateFnsDate,
} from './dateUtils';
import { DATE_FORMAT_SHORT } from '../constants/dateFormats';
import { getNightsFromRange } from './nightMapUtils';

const MAX_GROUP_SIZE = 9999;

const siteTypeOrder = {
    'HIKE TO': 0,
    MANAGEMENT: 1,
    'STANDARD ELECTRIC': 2,
    'STANDARD NONELECTRIC': 3,
    'TENT ONLY ELECTRIC': 4,
    'TENT ONLY NONELECTRIC': 5,
    'WALK TO': 6,
    'RV ELECTRIC': 7,
    'RV NONELECTRIC': 8,
    'BALL FIELD': 9,
    COURT: 10,
    'GROUP EQUESTRIAN': 11,
    'GROUP HIKE TO': 12,
    'GROUP PICNIC AREA': 13,
    'GROUP RV AREA NONELECTRIC': 14,
    'GROUP SHELTER ELECTRIC': 15,
    'GROUP SHELTER NONELECTRIC': 16,
    'GROUP STANDARD AREA ELECTRIC': 17,
    'GROUP STANDARD AREA NONELECTRIC': 18,
    'GROUP STANDARD ELECTRIC': 19,
    'GROUP STANDARD NONELECTRIC': 20,
    'GROUP TENT ONLY AREA': 21,
    'GROUP TENT ONLY AREA NONELECTRIC': 22,
    'GROUP WALK TO': 23,
    'OVERNIGHT SHELTER ELECTRIC': 24,
    'OVERNIGHT SHELTER NONELECTRIC': 25,
    PICNIC: 26,
    'SHELTER ELECTRIC': 27,
    'SHELTER NONELECTRIC': 28,
    'CABIN ELECTRIC': 29,
    'CABIN NONELECTRIC': 30,
    ANCHORAGE: 31,
    'BOAT IN': 32,
    'EQUESTRIAN ELECTRIC': 33,
    'EQUESTRIAN NONELECTRIC': 34,
    LOOKOUT: 35,
    MOORING: 36,
    PARKING: 37,
    YURT: 38,
};

const datesToRange = (start, end) => {
    if (start && end) {
        return `${formatUTCDateFnsDate(start, DATE_FORMAT_SHORT)} - ${formatUTCDateFnsDate(end, DATE_FORMAT_SHORT)}`;
    }
    return null;
};

export const sortSiteTypes = (row) => {
    function compare(a, b) {
        const aDate = new Date(a.startDate);
        const bDate = new Date(b.startDate);
        const diff = aDate.getTime() - bDate.getTime();
        return diff === 0
            ? siteTypeOrder[a.siteType.toUpperCase()] -
                  siteTypeOrder[b.siteType.toUpperCase()]
            : diff;
    }
    row.sort(compare);
};

const getCampsiteMinMaxPeople = (campsite, isSearchData) => {
    const camp = campsite;
    let attrs = [];
    if (isSearchData) {
        attrs = camp && camp.attributes ? camp.attributes : [];
    } else {
        attrs =
            !isNil(camp) && !isEmpty(camp.site_details_map) ? camp.site_details_map : [];
    }
    const attributes = {};
    forEach(attrs, (a) => {
        attributes[a.attribute_name] = a.attribute_value;
        return null;
    });

    const maxPeople = attributes['Max Num of People'];
    const minPeople = attributes['Min Num of People'];

    let hasRange = false;
    if (maxPeople || minPeople) {
        hasRange = true;
    }

    return { min: minPeople, max: maxPeople, hasRange };
};

export const generateTemplateGroupFeesDisplay = (
    groupFeesMap,
    isTemplateData = false
) => {
    const thresholdLevels = keys(groupFeesMap);
    thresholdLevels.sort((a, b) => a - b);

    const thresholdPrices = [];
    let prevPrice = 0.0;
    forEach(thresholdLevels, (level, i) => {
        const min = parseInt(level, 10);

        let thresholdPrice = 0.0;
        if (isTemplateData) {
            thresholdPrice = parseFloat(groupFeesMap[min].amount) + prevPrice;
        } else {
            thresholdPrice = parseFloat(groupFeesMap[min]) + prevPrice;
        }
        prevPrice = thresholdPrice;
        let max = min + 1;
        if (i + 1 <= thresholdLevels.length - 1) {
            max = parseInt(thresholdLevels[i + 1], 10) - 1;
        } else {
            max = 'max';
        }
        let displayRange = `${min} - ${max}`;

        if (isEqual(min, max)) {
            displayRange = min;
        }

        const thresholdText = `${displayRange} people: ${formatMoney(thresholdPrice)}`;
        thresholdPrices.push(thresholdText);
    });
    return thresholdPrices;
};

export const generateTemplateIncrementFeesDisplay = (
    incrementFeesMap,
    isTemplateData = false
) => {
    const thresholdLevels = keys(incrementFeesMap);
    thresholdLevels.sort((a, b) => a - b);

    const incrementPrices = [];
    let minPeople = null;
    let maxPeople = null;
    forEach(thresholdLevels, (level) => {
        const iLevel = parseInt(level, 10);
        let incrementPrice = 0.0;
        if (isTemplateData) {
            const maxThreshold = get(
                incrementFeesMap[iLevel],
                'max_threshold',
                MAX_GROUP_SIZE
            );
            incrementPrice = parseFloat(get(incrementFeesMap[iLevel], 'fee.amount', '0'));
            minPeople = parseInt(get(incrementFeesMap[iLevel], 'min_threshold', 0), 10);
            maxPeople = isEqual(maxThreshold, MAX_GROUP_SIZE)
                ? 'max'
                : parseInt(maxThreshold, 10);
        } else {
            incrementPrice = parseFloat(incrementFeesMap[iLevel]);
            minPeople = iLevel;
        }
        let display = null;
        if (!isEqual(incrementPrice, 0.0)) {
            let incrementRange = `${minPeople}`;
            incrementRange += !isNil(maxPeople) ? ` - ${maxPeople}` : `+`;
            display = `${incrementRange} people: ${formatMoney(incrementPrice)}/person`;
        }

        if (!isNil(display)) {
            incrementPrices.push(display);
        }
    });
    return incrementPrices;
};

export const getGroupFeeValues = (groupFees) => {
    const thresholdLevels = keys(groupFees);
    thresholdLevels.sort((a, b) => a - b);

    const thresholdPrices = [];
    let prevPrice = 0.0;
    forEach(thresholdLevels, (level) => {
        const thresholdPrice = parseFloat(groupFees[level]) + prevPrice;
        prevPrice = thresholdPrice;
        thresholdPrices.push(thresholdPrice);
    });

    return thresholdPrices;
};

const getGroupFeeRange = (minPeople, maxPeople, groupFeesMap) => {
    let minFee = 0.0;
    let maxFee = 0.0;

    const thresholdLevels = keys(groupFeesMap);
    thresholdLevels.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    forEach(thresholdLevels, (level) => {
        if (minPeople >= parseInt(level, 10)) {
            minFee += groupFeesMap[level];
        }

        if (maxPeople >= parseInt(level, 10)) {
            maxFee += groupFeesMap[level];
        }
    });

    return { minFee, maxFee };
};

export const calculateFeeRangeWithOptions = (fees, options = {}) => {
    let minFee = 0.0;
    let maxFee = 0.0;
    let incrementFeesDisplay = null;
    const depositFee = get(fees, 'deposit', 0);
    const downPaymentFee = get(fees, 'partial_payment', 0);
    const perNightFee = get(fees, 'per_night', 0);
    const perPersonFee = parseFloat(get(fees, 'per_person', 0));
    const groupFee = get(fees, 'group_fees', {});
    const groupThresholdFees = getGroupFeeValues(groupFee);
    const incrementFee = get(fees, 'increment_fees', {});
    const weekendFee = get(fees, 'weekend', 0);
    const holidayFee = get(fees, 'holiday', 0);

    minFee += options.ignoreDepositFee ? 0 : parseFloat(depositFee);
    maxFee += options.ignoreDepositFee ? 0 : parseFloat(depositFee);
    minFee += parseFloat(perNightFee);
    maxFee += parseFloat(perNightFee);

    if (options.withSizedThreshold) {
        const groupFeeRange = getGroupFeeRange(
            options.groupMin,
            options.groupMax,
            groupFee
        );
        minFee += groupFeeRange.minFee;
        maxFee += groupFeeRange.maxFee;
    } else if (size(groupThresholdFees) > 0) {
        const minThresholdFee = lodashMin(groupThresholdFees);
        const maxThresholdFee = lodashMax(groupThresholdFees);

        minFee += minThresholdFee;
        maxFee += maxThresholdFee;
    }

    if (options.withWeekend) {
        maxFee += parseFloat(weekendFee);
    }

    if (options.withHoliday) {
        maxFee += parseFloat(holidayFee);
    }

    if (options.withIncrement) {
        incrementFeesDisplay = generateTemplateIncrementFeesDisplay(incrementFee, false);
    }

    const roundedMin = round(minFee);
    const roundedMax = round(maxFee);
    const roundedPerPerson = round(perPersonFee);

    return {
        minFee,
        maxFee,
        perNightFee,
        perPersonFee,
        holidayFee,
        weekendFee,
        incrementFeesDisplay,
        roundedMin,
        roundedMax,
        roundedPerPerson,
        depositFee,
        downPaymentFee,
    };
};

const generateOptions = (fees, campsiteSizeData, params = {}) => {
    const { min, max, hasRange } = campsiteSizeData;
    const groupFees = get(fees, 'group_fees', {});
    const incrementFees = get(fees, 'increment_fees', {});
    const options = {};

    if (hasRange && !isEmpty(groupFees)) {
        options.groupMin = min;
        options.groupMax = max;
        options.withSizedThreshold = true;
    }

    if (!isEmpty(incrementFees)) {
        options.withIncrement = true;
    }

    if (params && params.ignoreDepositFee) {
        options.ignoreDepositFee = params.ignoreDepositFee;
    }

    return options;
};

const calculateCampsiteFeeRange = (fees, dates, campsiteSizeData, params = {}) => {
    const options = generateOptions(fees, campsiteSizeData, params);

    let feeData = calculateFeeRangeWithOptions(fees, options);
    const futureFees = get(fees, 'future_fees', {});
    const { start, end } = dates;
    const startDate = UTCStringToCalendarDate(start);
    const endDate = UTCStringToCalendarDate(end);

    if (!isNil(futureFees) && !isEmpty(futureFees)) {
        const futureEffectiveDate = get(futureFees, 'effective_date', null);
        const effectiveDate = isDateValid(futureEffectiveDate)
            ? UTCStringToCalendarDate(futureEffectiveDate)
            : null;
        const futureOptions = generateOptions(futureFees, campsiteSizeData, params);
        if (!isNil(effectiveDate)) {
            const futureFeeData = calculateFeeRangeWithOptions(futureFees, futureOptions);
            if (
                !isNil(startDate) &&
                !isNil(endDate) &&
                isDateBefore(startDate, effectiveDate) &&
                isDateAfter(endDate, effectiveDate)
            ) {
                feeData.futurePrices = futureFeeData;
            } else if (isDateSameOrBefore(effectiveDate, startDate)) {
                feeData = futureFeeData;
            }
        }
    }
    return feeData;
};

export const getPriceRates = (campsite, currentRates) => {
    let priceRates = null;

    if (!isEmpty(currentRates) && !isNil(currentRates)) {
        let campsiteType = get(campsite, 'type', null);
        const campsiteFeeTemplates = get(campsite, 'fee_templates', null);
        const campsiteRateType = get(
            campsiteFeeTemplates,
            currentRates.season_type,
            null
        );

        if (!isNil(campsiteRateType)) {
            campsiteType = campsiteRateType;
        }
        priceRates = currentRates.rate_map[campsiteType];
    }
    return priceRates;
};

export const convertDiscountToNewTotal = (feeDiscountArray, originalFeeTotal) => {
    let totalDiscount = 0;
    forEach(feeDiscountArray, (discountValue) => {
        totalDiscount += discountValue;
    });
    return subtract(originalFeeTotal, totalDiscount);
};

const getPriceDisplay = (campsiteFees, typeOfUse, perUnit) => {
    const {
        minFee,
        maxFee,
        perNightFee,
        perPersonFee,
        roundedMin,
        roundedMax,
        roundedPerPerson,
        downPaymentFee,
    } = campsiteFees;

    const perNightPrice = `${formatMoney(perNightFee)}`;
    let priceRange = `${formatMoney(minFee + (downPaymentFee || 0))}`;
    let roundedPriceRange = `${formatMoney(roundedMin)}`;

    if (!isEqual(minFee, maxFee) && maxFee > minFee) {
        priceRange += ` - ${formatMoney(maxFee)}`;
    }

    if (!isEqual(roundedMin, roundedMax) && roundedMax > roundedMin) {
        roundedPriceRange += ` - ${formatMoney(roundedMax)}`;
    }

    let unit = includes(typeOfUse, 'Day') ? `${perUnit} day` : `${perUnit} night`;

    let isPerPersonOnly = false;
    if (perPersonFee && perPersonFee > 0) {
        if (isEqual(minFee, 0) && isEqual(maxFee, 0)) {
            isPerPersonOnly = true;
            priceRange = `${formatMoney(perPersonFee)}`;
            unit = `${perUnit} person`;
            roundedPriceRange = `${formatMoney(roundedPerPerson)}`;
        }
    }

    return {
        isPerPersonOnly,
        maxPrice: maxFee || perPersonFee,
        minPrice: minFee || perPersonFee,
        perPersonPrice: perPersonFee,
        priceRangeText: priceRange,
        roundedPriceRangeText: roundedPriceRange,
        perNightPrice,
        roundedMin,
        roundedMax,
        roundedPerPerson,
        typeOfUse,
        unit,
    };
};

const buildPriceRange = (minNum, maxNum) => {
    const min = !isNaN(minNum) ? minNum : 0;
    const max = !isNaN(maxNum) ? maxNum : 0;
    const rMin = round(min);
    const rMax = round(max);

    const isMaxZero = isEqual(max, 0);

    let priceRangeText = `${formatMoney(min)}`;
    let rPriceRangeText = `${formatMoney(rMin)}`;

    if (!isEqual(min, max)) {
        priceRangeText += ` - ${formatMoney(max)}`;
    }

    if (!isEqual(rMin, rMax)) {
        rPriceRangeText += ` - ${formatMoney(rMax)}`;
    }

    return { priceRangeText, rPriceRangeText, maxZero: isMaxZero };
};

export const getOverallPriceData = (currentPriceInfo, futurePriceInfo) => {
    const minPrice = lodashMin([currentPriceInfo.minPrice, futurePriceInfo.minPrice]);
    const maxPrice = lodashMax([currentPriceInfo.maxPrice, futurePriceInfo.maxPrice]);
    const { priceRangeText, rPriceRangeText } = buildPriceRange(minPrice, maxPrice);

    const roundedMin = round(minPrice);
    const roundedMax = round(maxPrice);
    const minPersonPrice = lodashMin([
        currentPriceInfo.perPersonPrice,
        futurePriceInfo.perPersonPrice,
    ]);
    const maxPersonPrice = lodashMax([
        currentPriceInfo.perPersonPrice,
        futurePriceInfo.perPersonPrice,
    ]);

    const perPersonPrice = maxPersonPrice;
    const roundedPerPerson = round(perPersonPrice);
    const {
        priceRangeText: perPersonPriceRangeText,
        rPriceRangeText: rPerPersonPriceRangeText,
        maxZero,
    } = buildPriceRange(minPersonPrice, maxPersonPrice);

    return {
        isPersonOnly: currentPriceInfo.isPerPersonOnly && futurePriceInfo.isPerPersonOnly,
        minPrice,
        maxPrice,
        priceRangeText,
        roundedPriceRangeText: rPriceRangeText,
        roundedMin,
        roundedMax,
        roundedPerPerson,
        perPersonPrice,
        perPersonPriceRangeText: !maxZero ? perPersonPriceRangeText : null,
        rPerPersonPriceRangeText: !maxZero ? rPerPersonPriceRangeText : null,
        typeOfUse: currentPriceInfo.typeOfUse,
        unit: currentPriceInfo.unit,
    };
};

const getCampsitePriceDisplay = (params, perUnit = 'per') => {
    const { campsite, seasonDates, dates, priceData, campsiteFees } = params;
    const { start: checkin, end: checkout } = seasonDates;
    const futureCampsiteFees = get(campsiteFees, 'futurePrices', null);

    let currentPriceData = getPriceDisplay(campsiteFees, campsite.type_of_use, perUnit);
    let futurePriceData;

    let hasFuturePriceData = false;
    let showDisclaimer = false;
    let priceDisclaimer;
    if (!isNil(futureCampsiteFees)) {
        hasFuturePriceData = true;
        const currentPriceInfo = getPriceDisplay(campsiteFees, campsite.type_of_use, '/');
        const futurePriceInfo = getPriceDisplay(
            futureCampsiteFees,
            campsite.type_of_use,
            '/'
        );
        futurePriceData = {
            currentPriceInfo,
            currentPriceDateRange: datesToRange(
                checkin,
                priceData.future_fees.effective_date
            ),
            futurePriceInfo,
            futurePriceDateRange: datesToRange(
                priceData.future_fees.effective_date,
                checkout
            ),
        };
        showDisclaimer = true;
        priceDisclaimer =
            !isNil(dates.checkin) && !isNil(dates.checkout)
                ? '*Campsite fees vary over the selected dates.'
                : null;

        currentPriceData = getOverallPriceData(currentPriceInfo, futurePriceInfo);
    }

    return {
        currentPriceData,
        hasFuturePriceData,
        futurePriceData,
        showDisclaimer,
        priceDisclaimer,
    };
};

export const calculateEstimatePrice = (
    minFee,
    maxFee,
    dates,
    holidays,
    holidayFee,
    weekendFee,
    perPersonFee,
    isDayUse,
    perNightFee,
    depositFee
) => {
    const isSinglePrice =
        isEqual(minFee, maxFee) && isEqual(holidayFee, 0) && isEqual(weekendFee, 0);

    let minPrice = 0;
    let maxPrice = 0;
    let nights = 0;
    let holidayDatesCount = 0;

    const { start, end } = dates;
    const dateRange = getNightsFromRange(
        UTCStringToCalendarDate(start),
        UTCStringToCalendarDate(end)
    );

    let holidayDates = [];
    if (holidays) {
        holidayDates = Object.values(holidays).flat();
    }

    forEach(dateRange, (dateStr) => {
        const cDate = UTCStringToCalendarDate(dateStr);
        nights += 1;
        minPrice += minFee;
        maxPrice += maxFee;

        // Check for weekend fees
        if (
            (isDayUse && isDayUseWeekendDay(cDate)) ||
            (!isDayUse && isWeekendDay(cDate))
        ) {
            minPrice += weekendFee;
            maxPrice += weekendFee;
        }

        // Check for holiday fees
        const formattedDate = dateStr.replace('.000Z', 'Z');
        if (holidayDates.includes(formattedDate)) {
            holidayDatesCount += 1;
        }
    });

    // Add holiday fees
    minPrice += holidayFee * holidayDatesCount;
    maxPrice += holidayFee * holidayDatesCount;

    if (isSinglePrice) {
        return {
            maxPrice: minFee * nights,
            minPrice: minFee * nights,
            nightsFee: perNightFee * nights,
            perPersonFee,
            nights,
            depositFee,
        };
    }

    return { minPrice, maxPrice, perPersonFee, nights };
};

const getCampsitePriceEstimate = (params) => {
    const { campsite, priceData, dates, holidays, campsiteFees } = params;
    const {
        minFee,
        maxFee,
        perNightFee,
        depositFee,
        downPaymentFee,
        perPersonFee,
        weekendFee,
        holidayFee,
    } = campsiteFees;
    const futurePrices = get(campsiteFees, 'futurePrices', null);

    let currentEstimate = null;
    let futureEstimate = null;
    let doAverage = false;

    const isDayUse = campsite.type_of_use === 'Day';

    currentEstimate = calculateEstimatePrice(
        minFee,
        maxFee,
        {
            start: dates.checkin,
            end: dates.checkout,
        },
        holidays,
        holidayFee,
        weekendFee,
        perPersonFee,
        isDayUse,
        perNightFee,
        depositFee
    );

    if (!isNil(futurePrices) && !isEmpty(futurePrices)) {
        const futureEffectiveDate = get(priceData, 'future_fees.effective_date', null);
        const effectiveDate = isDateValid(futureEffectiveDate)
            ? UTCStringToCalendarDate(futureEffectiveDate)
            : null;
        if (!isNil(effectiveDate)) {
            currentEstimate = calculateEstimatePrice(
                minFee,
                maxFee,
                {
                    start: dates.checkin,
                    end: dates.checkout,
                },
                holidays,
                holidayFee,
                weekendFee,
                perPersonFee
            );

            const {
                minFee: futureMin,
                maxFee: futureMax,
                perPersonFee: futurePerPersonFee,
                holidayFee: futureHolidayFee,
                weekendFee: futureWeekendFee,
            } = futurePrices;

            futureEstimate = calculateEstimatePrice(
                futureMin,
                futureMax,
                {
                    start: calendarDateToUtcString(effectiveDate),
                    end: dates.checkout,
                },
                holidays,
                futureHolidayFee,
                futureWeekendFee,
                futurePerPersonFee
            );
        }
    }

    let minPrice = 0;
    let maxPrice = 0;
    let perPersonPrice = 0;
    let totalNights = 0;

    if (!isNil(currentEstimate)) {
        minPrice += !isNaN(currentEstimate.minPrice) ? currentEstimate.minPrice : 0;
        maxPrice += !isNaN(currentEstimate.maxPrice) ? currentEstimate.maxPrice : 0;
        if (currentEstimate.perPersonFee > perPersonPrice) {
            perPersonPrice = currentEstimate.perPersonFee;
        }
        totalNights += currentEstimate.nights;
    }

    if (!isNil(futureEstimate)) {
        minPrice += !isNaN(futureEstimate.minPrice) ? futureEstimate.minPrice : 0;
        maxPrice += !isNaN(futureEstimate.maxPrice) ? futureEstimate.maxPrice : 0;
        if (futureEstimate.perPersonFee > perPersonPrice) {
            perPersonPrice = futureEstimate.perPersonFee;
        }
        totalNights += futureEstimate.nights;
        if (!isNil(dates.checkin) && !isNil(dates.checkout)) {
            doAverage = true;
        }
    }

    let averageMin = 0;
    let averageMax = 0;
    if (doAverage) {
        averageMin = round(minPrice / totalNights);
        averageMax = round(maxPrice / totalNights);
    }

    const averageRange = buildPriceRange(averageMin, averageMax);

    return {
        minPrice,
        maxPrice,
        perPersonFee: perPersonPrice,
        totalNights,
        doAverage,
        averageRange,
        depositFee,
        downPaymentFee,
        nightsFee: perNightFee * totalNights,
    };
};

export const calculateCampsitePricing = (params, isSearchData = false) => {
    const { campsite, seasonDates, priceData, perUnit } = params;

    const campsiteSizeData = getCampsiteMinMaxPeople(campsite, isSearchData);
    let campsiteFees;
    if (campsite && !isEmpty(priceData)) {
        campsiteFees = calculateCampsiteFeeRange(
            priceData,
            seasonDates,
            campsiteSizeData,
            params
        );
    } else {
        return {
            hasPriceData: false,
        };
    }

    const feeParams = { campsiteFees, ...params };

    let displayData;
    if (params.display) {
        displayData = getCampsitePriceDisplay(feeParams, perUnit);
    }

    let estimateData;
    if (params.estimatePrice) {
        estimateData = getCampsitePriceEstimate(feeParams);
    }

    return {
        hasPriceData: true,
        campsiteFees,
        displayData,
        estimateData,
    };
};
